import React, { useEffect, useMemo, useState } from 'react';
import {
	Avatar,
	Box,
	Collapse,
	LinearProgress,
	LinearProgressProps,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AssignmentTurnedIn as AssignmentTurnedInIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { formatDateTimeToShowUser } from '../../../../../features/common/helpers';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

enum SaleOrderBillingStateItemInfoState {
	Pending = 1,
	InProgress = 2,
	FinishOk = 3,
	FinishWithError = 4,
}

enum EventType {
	InitialState = 'InitialState',
	Add = 'Add',
	Update = 'Update',
	Delete = 'Delete',
	Ping = 'Ping',
}

interface ISaleOrderSyncBillingStatusMetadata {
	id: string;
	dealId: number;
	isBitrixDeal: boolean;
	agencyId: number;
	agencyName: string;
	userNameTriggerSync: string;
}

enum CauseSetInSyncQueueEnum {
	SyncInQueueByUser = 1,
	SyncInQueueBySystemTask = 2,
}

interface ISaleOrderSyncBillingStatusItemInfo {
	id: string;
	metadata: ISaleOrderSyncBillingStatusMetadata;
	state: SaleOrderBillingStateItemInfoState;
	createdAt: string;
	show: boolean;
	causeSetInSyncQueue: CauseSetInSyncQueueEnum;
	maybeError?: string;
}

interface ISyncInitialEventStateSaleOrderBillingStatus {
	items: ISaleOrderSyncBillingStatusItemInfo[];
	eventType: EventType;
}

interface ISyncUpdateOrAddEventStateSaleOrderBillingStatus {
	items: ISaleOrderSyncBillingStatusItemInfo[];
	eventType: EventType;
}

interface ISyncDeleteEventStateSaleOrderBillingStatus {
	itemId: string;
	eventType: EventType;
	maybeError?: string;
}

// Styled components
const StyledList = styled(List)(({ theme }) => ({
	width: '100%',
	backgroundColor: theme.palette.background.paper,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
	paddingTop: theme.spacing(1),
	paddingBottom: theme.spacing(1),
	marginBottom: theme.spacing(2),
	border: `1px solid ${theme.palette.divider}`,
	borderRadius: theme.shape.borderRadius,
	backgroundColor: theme.palette.background.paper,
}));

const MAX_VISIBLE_ITEMS = 500;

const SalesOrderBillingSyncProcessStatus: React.FC = () => {
	const [items, setItems] = useState<ISaleOrderSyncBillingStatusItemInfo[]>([]);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const apiUrl = process.env.REACT_APP_SALES_ORDER_API_URL || '';
		const eventSource = new EventSource(`${apiUrl}events/sales-order-billing-status-sync`);

		eventSource.onmessage = (event: MessageEvent) => {
			try {
				const newEvent = JSON.parse(event.data);
				const { eventType } = newEvent as { eventType: EventType };
				console.log('event received:', newEvent);

				if (eventType === EventType.Ping) {
					return;
				} else if (eventType === EventType.InitialState) {
					const initialEvent = newEvent as ISyncInitialEventStateSaleOrderBillingStatus;
					setItems((prevEvents) => {
						const combinedEvents = [...prevEvents, ...initialEvent.items.map((item) => ({ ...item, show: true }))];
						return sortEventsByUserFirstThenOldest(combinedEvents);
					});
				} else if (eventType === EventType.Add || eventType === EventType.Update) {
					const addOrUpdateEvent = newEvent as ISyncUpdateOrAddEventStateSaleOrderBillingStatus;
					const itemsInAddOrUpdateEvent = addOrUpdateEvent.items.map((item) => ({ ...item, show: true }));
					setItems((prevEvents) => {
						const itemsExistBefore = [...prevEvents];
						if (itemsExistBefore.length === 0) {
							return sortEventsByUserFirstThenOldest(itemsInAddOrUpdateEvent);
						} else {
							const itemsToKeep = itemsExistBefore.filter(
								(item) => !itemsInAddOrUpdateEvent.some((x) => x.id === item.id),
							);
							return sortEventsByUserFirstThenOldest([...itemsInAddOrUpdateEvent, ...itemsToKeep]);
						}
					});
				} else if (eventType === EventType.Delete) {
					const deleteEvent = newEvent as ISyncDeleteEventStateSaleOrderBillingStatus;

					setItems((prevEvents) => {
						const index = prevEvents.findIndex((e) => e.id === deleteEvent.itemId);
						if (index !== -1) {
							const item = prevEvents[index];
							if (item.state === SaleOrderBillingStateItemInfoState.InProgress) {
								const updatedItem = { ...item, state: SaleOrderBillingStateItemInfoState.FinishOk };
								if (deleteEvent.maybeError) {
									updatedItem.maybeError = deleteEvent.maybeError;
									updatedItem.state = SaleOrderBillingStateItemInfoState.FinishWithError;
								}
								console.log(`Item with id ${updatedItem.id}  in progress, setting to state` + updatedItem.state);

								const updatedEvents = [...prevEvents];
								updatedEvents[index] = updatedItem;

								setTimeout(() => {
									setItems((events) => events.map((e) => (e.id === deleteEvent.itemId ? { ...e, show: false } : e)));
								}, 500);

								setTimeout(() => {
									setItems((events) => events.filter((e) => e.id !== deleteEvent.itemId));
								}, 2500);

								return updatedEvents;
							} else {
								return prevEvents.filter((e) => e.id !== deleteEvent.itemId);
							}
						} else {
							return prevEvents;
						}
					});
				}
			} catch (e) {
				console.error('Error parsing event sync billing status:', e);
			}
		};

		eventSource.onerror = (err) => {
			console.error('SSE connection error', err);
			setError('Ocurrió un error en la conexión con el servidor. Por favor actualice la página.');
			eventSource.close();
		};

		return () => {
			eventSource.close();
		};
	}, []);

	const byUserFirstThenOldest = (a: ISaleOrderSyncBillingStatusItemInfo, b: ISaleOrderSyncBillingStatusItemInfo) => {
		const pa = a.causeSetInSyncQueue === CauseSetInSyncQueueEnum.SyncInQueueByUser ? 0 : 1;
		const pb = b.causeSetInSyncQueue === CauseSetInSyncQueueEnum.SyncInQueueByUser ? 0 : 1;
		if (pa !== pb) return pa - pb;

		return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
	};

	const sortEventsByUserFirstThenOldest = (events: ISaleOrderSyncBillingStatusItemInfo[]) =>
		[...events].sort(byUserFirstThenOldest);

	const visibleItems = useMemo(() => items.slice(0, MAX_VISIBLE_ITEMS), [items]);

	const getProgressBarProps = (state: SaleOrderBillingStateItemInfoState): LinearProgressProps => {
		const baseProps: LinearProgressProps = {
			sx: { width: '100%', mt: 1 },
		};

		switch (state) {
			case SaleOrderBillingStateItemInfoState.Pending:
				return {
					...baseProps,
					variant: 'determinate',
					value: 0,
					color: 'info',
				};
			case SaleOrderBillingStateItemInfoState.InProgress:
				return {
					...baseProps,
					variant: 'indeterminate',
					color: 'primary',
				};

			case SaleOrderBillingStateItemInfoState.FinishOk:
				return {
					...baseProps,
					variant: 'determinate',
					value: 100,
					color: 'success',
				};
			case SaleOrderBillingStateItemInfoState.FinishWithError:
				return {
					...baseProps,
					variant: 'determinate',
					value: 100,
					color: 'error',
				};
			default:
				return {
					...baseProps,
					variant: 'determinate',
					value: 0,
					color: 'info',
				};
		}
	};

	const getStateTextInSpanish = (state: SaleOrderBillingStateItemInfoState): string => {
		switch (state) {
			case SaleOrderBillingStateItemInfoState.Pending:
				return 'Pendiente';
			case SaleOrderBillingStateItemInfoState.InProgress:
				return 'En Progreso';
			case SaleOrderBillingStateItemInfoState.FinishOk:
				return 'Finalizado Correctamente';
			case SaleOrderBillingStateItemInfoState.FinishWithError:
				return 'Finalizado con Error';
			default:
				return 'Desconocido';
		}
	};

	return (
		<Box p={2}>
			{error && <Typography color="error">{error}</Typography>}
			<Paper elevation={3} style={{ maxHeight: 500, overflow: 'auto', padding: '16px' }}>
				{items.length === 0 ? (
					<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4}>
						<AssignmentTurnedInIcon style={{ fontSize: 80, color: '#ccc' }} />
						<Typography variant="h6" color="textSecondary" align="center">
							No hay elementos procesándose en este momento.
						</Typography>
					</Box>
				) : (
					<>
						<StyledList>
							{visibleItems.map((event) => {
								const progressBarProps = getProgressBarProps(event.state);
								const title = `ID: ${event.id}`;
								const subtitle = `${event.metadata.isBitrixDeal ? 'Deal' : 'Caso'}: ${
									event.metadata.dealId
								} Agencia:  ${event.metadata.agencyName} • En cola desde: ${formatDateTimeToShowUser(
									event.createdAt,
								).substr(0, 16)} • Creado por: ${event.metadata.userNameTriggerSync}`;
								const stateText = getStateTextInSpanish(event.state);
								const listItemContent = (
									<>
										<ListItemAvatar>
											<Avatar>
												{event.state === SaleOrderBillingStateItemInfoState.FinishOk ? (
													<CheckCircleIcon color="success" />
												) : (
													<ReceiptLongIcon />
												)}
											</Avatar>
										</ListItemAvatar>
										<ListItemText primary={title} secondary={subtitle} />
										<Box sx={{ width: '50%', ml: 2 }}>
											<Typography variant="body2" align="center">
												{stateText}
											</Typography>
											<LinearProgress {...progressBarProps} sx={{ height: '15px' }} />
										</Box>
									</>
								);

								return (
									<Collapse key={event.id} in={event.show} timeout={{ enter: 500, exit: 1500 }} unmountOnExit>
										<StyledListItem>
											{event.state === SaleOrderBillingStateItemInfoState.FinishWithError && event.maybeError ? (
												<Tooltip title={event.maybeError}>{listItemContent}</Tooltip>
											) : (
												listItemContent
											)}
										</StyledListItem>
									</Collapse>
								);
							})}
						</StyledList>
						{items.length > MAX_VISIBLE_ITEMS && (
							<Box mt={1} display="flex" justifyContent="center">
								<Typography variant="caption" color="textSecondary">
									Mostrando {visibleItems.length} de {items.length} elementos.
								</Typography>
							</Box>
						)}
					</>
				)}
			</Paper>
		</Box>
	);
};

export default SalesOrderBillingSyncProcessStatus;
